import { useEffect } from 'react';
import { useCurrentPlanFeatures } from './useCurrentPlanFeatures';

import { useCurrentMonthVideoCount } from './useCurrentMonthVideoCount';

import {
  sendPlanFeaturesToExtension,
  sendRecordingLimitReached,
  calculateRecordingLimitReached,
} from '@/utils/extensionCommunication';
import { useAuthStore } from '@/stores/authStore';

export const usePlanFeatureSync = () => {
  const planInfo = useCurrentPlanFeatures();
  const userData = useAuthStore((state) => state.userData);

  // User is "on a team" (team-plan owner OR member) when they are on a team plan.
  // Read isTeamMember directly from API (authoritative source) instead of deriving from organization_id.
  // Default to false for safety (matches contract: fail open to non-team on missing flag).
  const isTeamMember = userData?.isTeamMember ?? false;

  // `videoCount`/`limit` from useCurrentMonthVideoCount return the correct quota for the
  // plan type: monthly count vs. limit for rate_based, or active_videos_used vs.
  // max_active_videos for lifetime_cap. Use those instead of planInfo.monthlyVideos so
  // lifetime-cap plans aren't mistakenly treated as unlimited just because monthlyVideos === 0.
  const { videoCount, limit: applicableLimit } = useCurrentMonthVideoCount();

  // Resolved unlimited plan = pricing data loaded, no error, plan name resolved (not the
  // 'Unknown' sentinel useCurrentPlanFeatures returns when pricing is missing) AND the
  // applicable limit for this plan type === 0.
  const isKnownUnlimitedPlan =
    !planInfo.isLoading && !planInfo.error && planInfo.planName !== 'Unknown' && applicableLimit === 0;

  useEffect(() => {
    if (!planInfo.isLoading) {
      sendPlanFeaturesToExtension({
        features: planInfo.features,
        // Encode unlimited (0) as a large finite cap. Legacy extension builds
        // (< 5.5.5276) lack the "0 == unlimited" guard and fail-closed on a falsy
        // monthlyVideos, blocking recording. Newer builds are unaffected, and the
        // web app's own limit logic uses applicableLimit (not this field).
        // Lifetime-cap plans also report monthlyVideos === 0 (that field isn't how
        // their limit is tracked) but are NOT unlimited — send their real cap
        // (applicableLimit) instead of the unlimited sentinel.
        monthlyVideos: planInfo.monthlyVideos || (planInfo.planType === 'lifetime_cap' ? applicableLimit : 999999),
        storageDuration: planInfo.storageDuration,
        planName: planInfo.planName,

        currentMonthlyUsage: videoCount,
        isTeamMember,
        videoDuration: planInfo.videoDuration,
        videoQuality: planInfo.videoQuality,
        video_seconds: planInfo.video_seconds,
        videoQualityNumber: planInfo.videoQualityNumber,
      });

      const limitReached = calculateRecordingLimitReached(videoCount, applicableLimit, isKnownUnlimitedPlan);
      sendRecordingLimitReached(limitReached);
    }
  }, [
    planInfo.isLoading,
    planInfo.features,
    planInfo.monthlyVideos,
    planInfo.storageDuration,
    planInfo.planName,
    planInfo.planType,
    isKnownUnlimitedPlan,
    videoCount,
    applicableLimit,
    isTeamMember,
  ]);

  return {
    planInfo,
    isRecordingLimitReached: calculateRecordingLimitReached(videoCount, applicableLimit, isKnownUnlimitedPlan),
  };
};
